import {
  DEFAULT_CURRENCY,
  cherrySymbolImg,
  goldNuggetSymbolImg,
  jackpotCrownSymbolImg,
  kunduDrumSymbolImg,
  kumulBirdSymbolImg,
  polCoinSymbolImg,
  diamondSymbolImg,
  btcCoinSymbolImg,
  lucky7SymbolImg,
  jackpotOrbSymbolImg,
} from "../../../../../static/Constants";
import { colors, borderRadiusPx, customShadows, THREESLOT_COLOR_THEME } from "../../../../../theme";

/**
 * 3-Slot Detail Page Theme & Layout Config (Centralized Tokens)
 */
export const THREE_SLOT_DETAILS_THEME = {
  pageBg: colors.dashboard.canvas,
  cardMaxWidth: 540,
  containerMaxWidth: "lg",
  headerBorderRadius: borderRadiusPx.xl,
  machineBorderRadius: borderRadiusPx.xxl,
  shadow: customShadows.card,
  hoverShadow: customShadows.cardHover,
  colorTheme: THREESLOT_COLOR_THEME,
};

/**
 * Centralized Progressive Jackpot Tier Themes (Mega, Major, Mini)
 */
export const JACKPOT_TIER_THEMES = {
  mega: {
    id: "mega",
    label: "👑 MEGA JACKPOT",
    background: "linear-gradient(180deg, #7f1d1d 0%, #450a0a 100%)",
    border: "1.5px solid #ef4444",
    borderColor: "#ef4444",
    boxShadow: "0 2px 8px rgba(239, 68, 68, 0.3)",
    titleColor: "#fca5a5",
    valueColor: "#fef08a",
    borderRadius: borderRadiusPx.md || "10px",
  },
  major: {
    id: "major",
    label: "💎 MAJOR JACKPOT",
    background: "linear-gradient(180deg, #581c87 0%, #3b0764 100%)",
    border: "1.5px solid #c084fc",
    borderColor: "#c084fc",
    boxShadow: "0 2px 8px rgba(192, 132, 252, 0.3)",
    titleColor: "#e9d5ff",
    valueColor: "#fef08a",
    borderRadius: borderRadiusPx.md || "10px",
  },
  mini: {
    id: "mini",
    label: "🍒 MINI JACKPOT",
    background: "linear-gradient(180deg, #064e3b 0%, #022c22 100%)",
    border: "1.5px solid #4ade80",
    borderColor: "#4ade80",
    boxShadow: "0 2px 8px rgba(74, 222, 128, 0.3)",
    titleColor: "#bbf7d0",
    valueColor: "#fef08a",
    borderRadius: borderRadiusPx.md || "10px",
  },
};

/**
 * Side Badge Numbers with Casino Neon Colors
 */
export const SIDE_BADGES = [
  { num: "4", color: colors.red[500] || "#ef4444" },
  { num: "2", color: colors.blue[500] || "#3b82f6" },
  { num: "9", color: colors.emerald[500] || "#22c55e" },
  { num: "6", color: colors.amber[500] || "#eab308" },
  { num: "1", color: colors.purple[500] || "#a855f7" },
  { num: "7", color: "#f97316" },
  { num: "8", color: colors.cyan[500] || "#06b6d4" },
  { num: "3", color: "#ec4899" },
  { num: "5", color: colors.blue[500] || "#3b82f6" },
];

/**
 * Slot Machine Arcade Frame Theme Tokens
 */
export const SLOT_MACHINE_FRAME_THEME = {
  bg: "#080d1a",
  headerBg: "#0b1220",
  border: "2px solid rgba(234, 179, 8, 0.5)",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.8), 0 0 30px rgba(234, 179, 8, 0.2)",
  borderRadius: { xs: borderRadiusPx.lg || "16px", sm: borderRadiusPx.xxl || "24px" },
  headerBorderRadius: borderRadiusPx.md || "8px",
  goldBorder: "rgba(234, 179, 8, 0.5)",
  goldText: "#fef08a",
  amberAccent: "#f59e0b",
};

/**
 * 3-SlotBET Pokies Game Configuration
 * Fiat & Crypto 5-Symbol Editions (Contract Mapped 0..4)
 */
export const THREE_SLOT_CONFIG = {
  name: "3-SlotBET",
  subtitle: "PROGRESSIVE POKIES",
  reels: 3,
  rows: 3,
  paylines: 1,
  paylineDescription: "Match 3 symbols on the center payline to win!",
  initialReels: ["cherry", "gold_nugget", "jackpot_crown"],
  defaultBet: 5.0,
  minBet: 1.0,
  maxBet: 500.0,
  betSteps: [1, 2, 5, 10, 25, 50, 100, 250, 500],
  currencySymbol: DEFAULT_CURRENCY || "POL",
  currencyName: DEFAULT_CURRENCY || "POL",
};

export const JACKPOT_DEFAULTS = {
  mega: 50000.0,
  major: 10000.0,
  mini: 1000.0,
};

export const THEME_OPTIONS = [
  {
    id: "fiat",
    label: "Fiat Pokies",
    description: "Fiat symbols: Cherry (Mini), Gold Nugget (Major), Crown (Mega), Drum (5 Spins), Kumul Bird (10 Super Spins)",
  },
  {
    id: "crypto",
    label: "Crypto Pokies",
    description: "Crypto symbols: POL (Mini), Diamond (Major), BTC (Mega), Lucky 7 (5 Spins), Jackpot Orb (10 Super Spins)",
  },
];

/**
 * FIAT Pokies 5-Symbol Set (Contract Mapped 0 to 4)
 * 0: Mini Jackpot (Min Bet) -> Cherry
 * 1: Major Jackpot -> Gold Nugget
 * 2: Mega Jackpot -> Royal Crown
 * 3: 5 Free Spins -> Kundu Drum
 * 4: 10 Super Free Spins -> Kumul Bird
 */
export const FIAT_SET_SYMBOLS = [
  {
    contractNumber: 0,
    id: "cherry",
    label: "Cherry",
    winningTier: "Mini Jackpot",
    winOutcome: "Mini Bet / Mini Jackpot",
    code: "CHERRY",
    symbolText: "🍒",
    iconText: "🍒",
    multiplier: 10,
    tier: "Mini Jackpot",
    tierColor: "#10b981",
    color: "#ef4444",
    type: "fiat",
    category: "fiat",
    image: cherrySymbolImg,
    payout: "Mini Jackpot (Min Bet Win)",
    description: "Match 3 Cherries to win Mini Progressive Jackpot & Min Bet payout",
  },
  {
    contractNumber: 1,
    id: "gold_nugget",
    label: "Gold Nugget",
    winningTier: "Major Jackpot",
    winOutcome: "Major Jackpot",
    code: "GOLD",
    symbolText: "🪙",
    iconText: "🪙",
    multiplier: 30,
    tier: "Major Jackpot",
    tierColor: "#3b82f6",
    color: "#3b82f6",
    type: "fiat",
    category: "fiat",
    image: goldNuggetSymbolImg,
    payout: "Major Jackpot",
    description: "Match 3 Gold Nuggets to win Major Progressive Jackpot",
  },
  {
    contractNumber: 2,
    id: "jackpot_crown",
    label: "Royal Crown",
    winningTier: "Mega Jackpot",
    winOutcome: "Mega Jackpot",
    code: "CROWN",
    symbolText: "👑",
    iconText: "👑",
    multiplier: 50,
    tier: "Mega Jackpot",
    tierColor: "#f59e0b",
    color: "#d97706",
    type: "fiat",
    category: "fiat",
    image: jackpotCrownSymbolImg,
    payout: "Mega Jackpot",
    description: "Match 3 Crowns to win Grand Mega Progressive Jackpot",
  },
  {
    contractNumber: 3,
    id: "kundu_drum",
    label: "Kundu Drum",
    winningTier: "5 Free Spins",
    winOutcome: "5 Free Spins",
    code: "DRUM",
    symbolText: "🪘",
    iconText: "🪘",
    multiplier: 15,
    tier: "5 Free Spins",
    tierColor: "#8b5cf6",
    color: "#8b5cf6",
    type: "fiat",
    category: "fiat",
    image: kunduDrumSymbolImg,
    payout: "5 Free Spins",
    description: "Match 3 Kundu Drums to trigger 5 Free Spins Bonus Round",
  },
  {
    contractNumber: 4,
    id: "kumul_bird",
    label: "Kumul Bird",
    winningTier: "10 Super Spins",
    winOutcome: "10 Super Spins (2x Win)",
    code: "BIRD",
    symbolText: "🦅",
    iconText: "🦅",
    multiplier: 25,
    tier: "10 Super Spins",
    tierColor: "#dc2626",
    color: "#dc2626",
    type: "fiat",
    category: "fiat",
    image: kumulBirdSymbolImg,
    payout: "10 Super Spins (2x)",
    description: "Match 3 Kumul Birds for 10 Super Free Spins + 2x Win Multiplier",
  },
];

/**
 * CRYPTO Pokies 5-Symbol Set (Contract Mapped 0 to 4)
 * 0: Mini Jackpot (Min Bet) -> Polygon POL
 * 1: Major Jackpot -> Diamond
 * 2: Mega Jackpot -> Bitcoin BTC
 * 3: 5 Free Spins -> Lucky 7
 * 4: 10 Super Free Spins -> Jackpot Orb
 */
export const CRYPTO_SET_SYMBOLS = [
  {
    contractNumber: 0,
    id: "pol",
    label: "Polygon POL",
    winningTier: "Mini Jackpot",
    winOutcome: "Mini Bet / Mini Jackpot",
    code: "POL",
    symbolText: "🟣",
    iconText: "🟣",
    multiplier: 10,
    tier: "Mini Jackpot",
    tierColor: "#10b981",
    color: "#10b981",
    type: "crypto",
    category: "crypto",
    image: polCoinSymbolImg,
    payout: "Mini Jackpot (Min Bet Win)",
    description: "Match 3 POL to win Mini Progressive Jackpot & Min Bet payout",
  },
  {
    contractNumber: 1,
    id: "diamond",
    label: "Diamond",
    winningTier: "Major Jackpot",
    winOutcome: "Major Jackpot",
    code: "DIAMOND",
    symbolText: "💎",
    iconText: "💎",
    multiplier: 30,
    tier: "Major Jackpot",
    tierColor: "#3b82f6",
    color: "#3b82f6",
    type: "crypto",
    category: "crypto",
    image: diamondSymbolImg,
    payout: "Major Jackpot",
    description: "Match 3 Diamonds to win Major Progressive Jackpot",
  },
  {
    contractNumber: 2,
    id: "btc",
    label: "Bitcoin BTC",
    winningTier: "Mega Jackpot",
    winOutcome: "Mega Jackpot",
    code: "BTC",
    symbolText: "₿",
    iconText: "₿",
    multiplier: 50,
    tier: "Mega Jackpot",
    tierColor: "#f59e0b",
    color: "#d97706",
    type: "crypto",
    category: "crypto",
    image: btcCoinSymbolImg,
    payout: "Mega Jackpot",
    description: "Match 3 Bitcoins to win Grand Mega Progressive Jackpot",
  },
  {
    contractNumber: 3,
    id: "lucky_7",
    label: "Lucky 7",
    winningTier: "5 Free Spins",
    winOutcome: "5 Free Spins",
    code: "SEVEN",
    symbolText: "7️⃣",
    iconText: "7️⃣",
    multiplier: 15,
    tier: "5 Free Spins",
    tierColor: "#8b5cf6",
    color: "#8b5cf6",
    type: "crypto",
    category: "crypto",
    image: lucky7SymbolImg,
    payout: "5 Free Spins",
    description: "Match 3 Lucky 7s to trigger 5 Free Spins Web3 Bonus Round",
  },
  {
    contractNumber: 4,
    id: "jackpot_orb",
    label: "Jackpot Orb",
    winningTier: "10 Super Spins",
    winOutcome: "10 Super Spins (2x Win)",
    code: "ORB",
    symbolText: "🔮",
    iconText: "🔮",
    multiplier: 25,
    tier: "10 Super Spins",
    tierColor: "#dc2626",
    color: "#dc2626",
    type: "crypto",
    category: "crypto",
    image: jackpotOrbSymbolImg,
    payout: "10 Super Spins (2x)",
    description: "Match 3 Jackpot Orbs for 10 Super Free Spins + 2x Win Multiplier",
  },
];

export const ALL_SYMBOLS = [...FIAT_SET_SYMBOLS, ...CRYPTO_SET_SYMBOLS];
export const SYMBOLS = ALL_SYMBOLS;

/**
 * Helper to get the 5 symbols mapped from 0 to 4 based on Fiat or Crypto category
 */
export const getSymbolsByCategory = (category = "fiat") => {
  if (category === "crypto") {
    return CRYPTO_SET_SYMBOLS;
  }
  return FIAT_SET_SYMBOLS;
};

export const getSymbolsByTheme = (themeName = "fiat") => {
  return getSymbolsByCategory(themeName);
};

export const getRandomSymbol = (category = "fiat") => {
  const symbolSet = getSymbolsByCategory(category);
  const randomIndex = Math.floor(Math.random() * symbolSet.length);
  return symbolSet[randomIndex] || symbolSet[0];
};

export const getSymbolById = (id) => {
  return ALL_SYMBOLS.find((symbol) => symbol.id === id) || FIAT_SET_SYMBOLS[0];
};

/**
 * Determine winning payout and jackpot status for 3-Reel Pokies
 * Evaluates center 3 symbols (Match 3)
 */
export const calculateWinResult = (
  reels,
  betAmount = 5.0,
  isFreeSpins = false,
  jackpots = JACKPOT_DEFAULTS
) => {
  if (!reels || reels.length !== 3) {
    return { isWin: false, amount: 0, type: "none", label: "No Spin" };
  }

  const [s1, s2, s3] = reels;
  const multiplierMultiplier = isFreeSpins ? 2 : 1; // 2x boost during Free Spins

  // Check 3 of a kind exact match
  const isMatch3 =
    (s1.contractNumber !== undefined &&
      s1.contractNumber === s2.contractNumber &&
      s2.contractNumber === s3.contractNumber) ||
    (s1.id && s1.id === s2.id && s2.id === s3.id);

  if (isMatch3) {
    const contractNum = s1.contractNumber;

    // Index 0: Mini Jackpot (Min Bet Win)
    if (contractNum === 0 || s1.id === "cherry" || s1.id === "pol") {
      const winAmt =
        jackpots?.mini && jackpots.mini > 0
          ? jackpots.mini
          : betAmount * s1.multiplier * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "jackpot",
        jackpotTier: "mini",
        label: "MINI JACKPOT!",
        symbol: s1,
      };
    }

    // Index 1: Major Jackpot
    if (contractNum === 1 || s1.id === "gold_nugget" || s1.id === "diamond") {
      const winAmt =
        jackpots?.major && jackpots.major > 0
          ? jackpots.major
          : betAmount * s1.multiplier * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "jackpot",
        jackpotTier: "major",
        label: "MAJOR JACKPOT!",
        symbol: s1,
      };
    }

    // Index 2: Mega Jackpot
    if (contractNum === 2 || s1.id === "jackpot_crown" || s1.id === "btc") {
      const winAmt =
        jackpots?.mega && jackpots.mega > 0
          ? jackpots.mega
          : betAmount * s1.multiplier * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "jackpot",
        jackpotTier: "mega",
        label: "MEGA JACKPOT!",
        symbol: s1,
      };
    }

    // Index 3: 5 Free Spins
    if (contractNum === 3 || s1.id === "kundu_drum" || s1.id === "lucky_7") {
      const winAmt = betAmount * s1.multiplier * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "freespins",
        freeSpinsWon: 5,
        label: "5 FREE SPINS WON!",
        jackpotTier: null,
        symbol: s1,
      };
    }

    // Index 4: 10 Super Free Spins (2x Multiplier)
    if (contractNum === 4 || s1.id === "kumul_bird" || s1.id === "jackpot_orb") {
      const winAmt = betAmount * s1.multiplier * 2 * multiplierMultiplier;
      return {
        isWin: true,
        amount: winAmt,
        type: "freespins",
        freeSpinsWon: 10,
        label: "10 SUPER SPINS (2X WIN)!",
        jackpotTier: null,
        symbol: s1,
      };
    }

    // Fallback match 3
    const winAmt = betAmount * (s1.multiplier || 10) * multiplierMultiplier;
    return {
      isWin: true,
      amount: winAmt,
      type: "match3",
      jackpotTier: null,
      label: `3x ${s1.label.toUpperCase()}`,
      symbol: s1,
    };
  }

  // No match
  return {
    isWin: false,
    amount: 0,
    type: "loss",
    jackpotTier: null,
    label: "No Match",
  };
};